import { jsPDF as JsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * change the , value to the csv value
 * @param {any} value value
 * @returns {any} formatted string
 */
const escapeCsvValue = (value: any) => {
  // Convert the value to a string, wrap it in quotes if it contains commas or quotes
  if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
    return `"${value.replace(/"/g, '""')}"`; // Escape quotes by doubling them
  }
  return value;
};
/**
 * Export the CSV file
 * @param {any} headers headers
 * @param {any} rows rows
 * @param {any} fileName fileName
 */
export const exportCSV = (headers: string[], rows: string[][], fileName: string = 'data.csv') => {
  const data = rows.map((row) => row.map((value) => escapeCsvValue(value)));
  const csvContent = [headers, ...data].map((event_: any) => event_.join(',')).join('\n');

  // Create a Blob from the CSV content with UTF-8 BOM
  const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csvContent], {
    type: 'text/csv;charset=utf-8;',
  });

  // Create a link and trigger the download
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute('download', fileName); // Set the filename
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link); // Cleanup
};

/**
 * Export the PDF file
 * @param {any} headers headers
 * @param {any} rows rows
 * @param {any} fileName fileName
 */
export const exportPDF = async (
  headers: string[],
  rows: string[][],
  fileName: string = 'generated.pdf',
) => {
  const document = new JsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4',
  });

  try {
    // Dynamically fetch the font from the public folder to minimize bundle size
    const response = await fetch('/fonts/Amiri-Regular.ttf');
    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const length = bytes.byteLength;
    for (let index = 0; index < length; index += 1) {
      binary += String.fromCharCode(bytes[index]);
    }
    const fontBase64 = window.btoa(binary);

    // Add Arabic font support
    document.addFileToVFS('Amiri-Regular.ttf', fontBase64);
    document.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
  } catch (error) {
    console.error('Failed to load Arabic font:', error);
  }

  /**
   * Processing arabic text for pdf
   * @param {any} text - The text to process
   * @returns {any} The processed text
   */
  const processText = (text: any) => {
    const textString = String(text || '');
    if (typeof (document as any).processArabic === 'function') {
      return (document as any).processArabic(textString);
    }
    return textString;
  };

  const processedHeaders = headers.map(processText);
  const processedRows = rows.map((row) => row.map(processText));

  autoTable(document, {
    head: [processedHeaders],
    body: processedRows,
    styles: {
      font: 'Nunito',
      fontSize: 6,
      cellPadding: 2,
      lineWidth: 0.2, // Thin borders
      lineColor: '#000',
      halign: 'center',
      valign: 'middle',
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      lineWidth: 0.2,
      fontSize: 6,
      fontStyle: 'normal',
    },
    tableLineWidth: 0.2,
    tableLineColor: '#000',
    margin: { top: 30, left: 10, right: 10 },
    theme: 'grid',
    startY: 20,
    /**
     * Dynamically taking page size
     * @param {any} data - The data object containing doc and pageNumber
     * @param {any} data.doc - The PDF document instance
     * @param {any} data.pageNumber - The current page number
     */
    didDrawPage: ({ doc, pageNumber }) => {
      const { width, height } = doc.internal.pageSize;
      document.setFontSize(8);
      doc.text(`Page ${pageNumber}`, width - 50, height - 10);
    },
  });

  document.save(fileName);
};
